import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal, Spinner, Table } from "react-bootstrap";
import { FiPlus, FiTrash2, FiRefreshCw, FiSlash, FiCheckCircle, FiEdit2 } from "react-icons/fi";
import { usePermissions } from "@features/entity/hooks/usePermissions";
import {
  DOCUMENT_CATEGORY_MANAGE_CODES,
  getDocumentCategoryCodesForOrigin,
  getDocumentCategoryCreateCode,
  getDocumentCategoryOriginUpdateCode,
} from "@features/entity/types/actionCodes";
import {
  createDepartmentReportDocument,
  disableDepartmentReportDocument,
  enableDepartmentReportDocument,
  queryDepartmentReportDocuments,
  removeDepartmentReportDocument,
  updateDepartmentReportDocument,
  type DepartmentReportDocument,
  type UpsertDepartmentReportDocumentPayload,
} from "@features/department/api/departmentReportDocumentApi";
import DepartmentReportDocumentModal from "@features/department/components/DepartmentReportDocumentModal";
import ChangeDepartmentReportDocumentOriginModal from "@features/department/components/ChangeDepartmentReportDocumentOriginModal";
import { SearchBar, type FilterField } from "@core/components/SearchBar";
import { useDebounce } from "@core/hooks/useDebounce";
import PaginationBar from "@core/components/PaginationBar";
import TablePaginationFooter, { getPaginationRange } from "@core/components/TablePaginationFooter";
import StatusPill from "@core/components/StatusPill";
import TableActions from "@core/components/TableActions";
import TableActionButton from "@core/components/TableActionButton";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import type { ErrorResponse } from "@core/types/errorResponse";
import type { OriginScope } from "@features/report/template/types/reportTemplate";

const PER_PAGE = 20;

const ORIGIN_SCOPE_LABELS: Record<string, string> = {
  CHURCH: "Church",
  DISTRICT: "District",
  NATION: "Nation",
};

const DOCUMENT_FILTER_FIELDS: FilterField[] = [
  { name: "name", label: "Name", type: "text", placeholder: "Filter by name", colWidth: 6 },
  { name: "description", label: "Help text", type: "text", placeholder: "Filter by help text", colWidth: 6 },
  {
    name: "originScope",
    label: "To be completed by",
    type: "select",
    options: [
      { value: "CHURCH", label: "Church" },
      { value: "DISTRICT", label: "District" },
      { value: "NATION", label: "Nation" },
    ],
    colWidth: 6,
  },
  {
    name: "isActive",
    label: "Status",
    type: "select",
    options: [
      { value: "1", label: "Active" },
      { value: "0", label: "Inactive" },
    ],
    colWidth: 6,
  },
];

type Props = {
  show: boolean;
  onHide: () => void;
  nationId: number;
  departmentId: number;
  departmentName: string;
};

export default function DepartmentReportDocumentsModal({
  show,
  onHide,
  nationId,
  departmentId,
  departmentName,
}: Props) {
  const showError = useApiError();

  const { hasPermission } = usePermissions({
    scopeType: "NATION",
    scopeId: nationId || undefined,
    actionCodes: [...DOCUMENT_CATEGORY_MANAGE_CODES],
  });

  const availableOriginScopes = useMemo(
    () =>
      (["CHURCH", "DISTRICT", "NATION"] as OriginScope[]).filter((scope) =>
        hasPermission(getDocumentCategoryCreateCode(scope))
      ),
    [hasPermission]
  );
  const canCreateAny = availableOriginScopes.length > 0;

  const [slots, setSlots] = useState<DepartmentReportDocument[]>([]);
  const [locked, setLocked] = useState<boolean | null>(null);
  const [lockedReason, setLockedReason] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editor, setEditor] = useState<DepartmentReportDocument | null | "new">(null);
  const [removeTarget, setRemoveTarget] = useState<DepartmentReportDocument | null>(null);
  const [originTarget, setOriginTarget] = useState<DepartmentReportDocument | null>(null);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE);
  const [total, setTotal] = useState(0);
  const [from, setFrom] = useState<number | null>(null);

  const debouncedQuery = useDebounce(searchQuery, 300);
  const appliedFilters = Object.fromEntries(
    Object.entries(activeFilters).filter(([, v]) => v !== "" && v != null)
  );
  const activeFilterCount = Object.keys(appliedFilters).length;
  const hasSearch = !!(debouncedQuery || activeFilterCount);

  const nestedOpen = editor != null || removeTarget != null || originTarget != null;
  const readOnly = !DOCUMENT_CATEGORY_MANAGE_CODES.some((code) => hasPermission(code));

  const load = useCallback(async () => {
    if (!nationId || !departmentId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const params: Record<string, unknown> = {
        page,
        perPage: PER_PAGE,
      };
      if (!hasSearch) {
        params.all = true;
      } else {
        if (debouncedQuery) params.freetext = debouncedQuery;
        if (appliedFilters.name) params.name = appliedFilters.name;
        if (appliedFilters.description) params.description = appliedFilters.description;
        if (appliedFilters.originScope) params.originScope = appliedFilters.originScope;
        if (appliedFilters.isActive === "1" || appliedFilters.isActive === true) {
          params.isActive = true;
        } else if (appliedFilters.isActive === "0" || appliedFilters.isActive === false) {
          params.isActive = false;
        }
      }

      const result = await queryDepartmentReportDocuments(
        nationId,
        departmentId,
        params as any
      );
      if (!result.isSuccess()) {
        const err = result.getError() as ErrorResponse;
        setLoadError(err?.detail || "Department not found");
        setSlots([]);
        setLocked(null);
        setLockedReason(null);
        return;
      }
      const payload = result.getData();
      setSlots(payload?.data ?? []);
      setLocked(Boolean(payload?.locked));
      setLockedReason(payload?.locked_reason ?? null);
      setCurrentPage(payload?.current_page ?? 1);
      setLastPage(payload?.last_page ?? 1);
      setPerPage(payload?.per_page ?? PER_PAGE);
      setTotal(payload?.total ?? 0);
      setFrom(payload?.from ?? null);
    } finally {
      setLoading(false);
    }
  }, [nationId, departmentId, page, hasSearch, debouncedQuery, appliedFilters.name, appliedFilters.description, appliedFilters.originScope, appliedFilters.isActive]);

  useEffect(() => {
    if (!show || !nationId || !departmentId) return;
    void load();
  }, [show, nationId, departmentId, load]);

  useEffect(() => {
    if (show) return;
    setEditor(null);
    setRemoveTarget(null);
    setOriginTarget(null);
    setLoadError(null);
    setSlots([]);
    setLocked(null);
    setLockedReason(null);
    setPage(1);
    setSearchQuery("");
    setActiveFilters({});
  }, [show]);

  const nextListOrder = useMemo(
    () => (slots.length === 0 ? 0 : Math.max(...slots.map((s) => s.list_order)) + 1),
    [slots]
  );

  const writesDisabled = locked !== false || readOnly;
  const { start, end } = getPaginationRange({
    from,
    page: currentPage,
    perPage,
    total,
    currentCount: slots.length,
  });

  const handleParentHide = () => {
    if (nestedOpen || busy) return;
    onHide();
  };

  const handleSave = async (payload: UpsertDepartmentReportDocumentPayload) => {
    if (!nationId || !departmentId) return;
    setBusy(true);
    try {
      const result =
        editor && editor !== "new"
          ? await updateDepartmentReportDocument(nationId, departmentId, editor.department_report_document_id, payload)
          : await createDepartmentReportDocument(nationId, departmentId, payload);
      if (!result.isSuccess()) {
        showError(result.getError() as ErrorResponse);
        return;
      }
      showSuccess(result.getData()?.message ?? "Saved.");
      setEditor(null);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!nationId || !departmentId || !removeTarget) return;
    setBusy(true);
    try {
      const result = await removeDepartmentReportDocument(
        nationId,
        departmentId,
        removeTarget.department_report_document_id
      );
      if (!result.isSuccess()) {
        showError(result.getError() as ErrorResponse);
        return;
      }
      showSuccess(result.getData()?.message ?? "Removed.");
      setRemoveTarget(null);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const handleToggleActive = async (slot: DepartmentReportDocument, enable: boolean) => {
    if (!nationId || !departmentId) return;
    setBusy(true);
    try {
      const result = enable
        ? await enableDepartmentReportDocument(nationId, departmentId, slot.department_report_document_id)
        : await disableDepartmentReportDocument(nationId, departmentId, slot.department_report_document_id);
      if (!result.isSuccess()) {
        showError(result.getError() as ErrorResponse);
        return;
      }
      showSuccess(result.getData()?.message ?? (enable ? "Enabled." : "Disabled."));
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleParentHide}
        size="xl"
        centered
        scrollable
        keyboard={!nestedOpen && !busy}
        data-testid="department-report-documents-modal"
      >
        <Modal.Header closeButton={!nestedOpen && !busy}>
          <Modal.Title>{readOnly ? "View report document categories" : "Manage report document categories"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-start gap-3 mb-3">
            <p className="text-muted mb-0">
              {departmentName
                ? `List the document categories users should attach on ${departmentName} reports.`
                : "List the document categories users should attach on this department's reports."}{" "}
              Names must be unique per department. Users can always upload extras under Other on the report.
            </p>
            {canCreateAny && locked === false ? (
              <Button
                variant="primary"
                className="flex-shrink-0"
                onClick={() => setEditor("new")}
                data-testid="add-report-document-button"
              >
                <FiPlus className="me-2" aria-hidden />
                Add document category
              </Button>
            ) : null}
          </div>

          {locked === true && lockedReason ? (
            <Alert variant="warning" data-testid="documents-locked-reason">
              {lockedReason}
            </Alert>
          ) : null}

          {loadError ? <Alert variant="danger">{loadError}</Alert> : null}

          <div className="ntcog-list-search-section mb-3">
            <SearchBar
              placeholder="Search document categories…"
              filterFields={DOCUMENT_FILTER_FIELDS}
              initialQuery={searchQuery}
              activeFilterCount={activeFilterCount}
              loading={loading}
              onQueryChange={(q) => {
                setSearchQuery(q);
                setPage(1);
              }}
              onFiltersApply={(f) => {
                setActiveFilters(f);
                setPage(1);
              }}
              onClear={() => {
                setSearchQuery("");
                setActiveFilters({});
                setPage(1);
              }}
              testId="document-category-search-bar"
            />
          </div>

          {loading ? (
            <div className="py-4">
              <Spinner animation="border" size="sm" className="me-2" aria-hidden />
              Loading…
            </div>
          ) : (
            <Table responsive hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Help text</th>
                  <th>To be completed by</th>
                  <th>Required</th>
                  <th>Status</th>
                  <th>Order</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {slots.length === 0 && !loadError ? (
                  <tr>
                    <td colSpan={7} className="text-muted">
                      No document categories found. Try adjusting filters or add a category.
                    </td>
                  </tr>
                ) : (
                  slots.map((slot) => {
                    const codes = getDocumentCategoryCodesForOrigin(slot.origin_scope);
                    const canEdit = codes ? hasPermission(codes.update) : false;
                    const canRemove = codes ? hasPermission(codes.remove) : false;
                    const canEnable = codes ? hasPermission(codes.enable) : false;
                    const canDisable = codes ? hasPermission(codes.disable) : false;
                    const canChangeOrigin =
                      slot.is_active && hasPermission(getDocumentCategoryOriginUpdateCode(slot.origin_scope));

                    return (
                      <tr key={slot.department_report_document_id}>
                        <td className="fw-medium">{slot.name}</td>
                        <td className="text-muted small">{slot.description || "—"}</td>
                        <td>{ORIGIN_SCOPE_LABELS[slot.origin_scope] || slot.origin_scope}</td>
                        <td>
                          {slot.is_required
                            ? `Yes${slot.required_at ? ` starting ${slot.required_at}` : ""}`
                            : "No"}
                        </td>
                        <td>
                          <StatusPill active={!!slot.is_active} />
                        </td>
                        <td>{slot.list_order}</td>
                        <td className="text-end">
                          <TableActions aria-label={`Actions for ${slot.name}`}>
                            {canEdit && locked === false ? (
                              <TableActionButton
                                icon={<FiEdit2 aria-hidden />}
                                label="Edit"
                                primary
                                variant="outline-secondary"
                                onClick={() => setEditor(slot)}
                                data-testid={`edit-document-category-button-${slot.department_report_document_id}`}
                              />
                            ) : null}
                            {canChangeOrigin && locked === false ? (
                              <TableActionButton
                                icon={<FiRefreshCw aria-hidden />}
                                label="Change who uploads this"
                                variant="outline-secondary"
                                onClick={() => setOriginTarget(slot)}
                                data-testid={`change-document-origin-button-${slot.department_report_document_id}`}
                              />
                            ) : null}
                            {slot.is_active && canDisable && locked === false ? (
                              <TableActionButton
                                icon={<FiSlash aria-hidden />}
                                label="Disable"
                                variant="outline-secondary"
                                className="ntcog-table-action-btn--danger"
                                onClick={() => void handleToggleActive(slot, false)}
                                data-testid={`disable-document-category-button-${slot.department_report_document_id}`}
                              />
                            ) : null}
                            {!slot.is_active && canEnable && locked === false ? (
                              <TableActionButton
                                icon={<FiCheckCircle aria-hidden />}
                                label="Enable"
                                variant="outline-secondary"
                                className="ntcog-table-action-btn--success"
                                onClick={() => void handleToggleActive(slot, true)}
                                data-testid={`enable-document-category-button-${slot.department_report_document_id}`}
                              />
                            ) : null}
                            {canRemove && locked === false ? (
                              <TableActionButton
                                icon={<FiTrash2 aria-hidden />}
                                label="Remove"
                                variant="outline-secondary"
                                className="ntcog-table-action-btn--danger"
                                onClick={() => setRemoveTarget(slot)}
                                data-testid={`remove-document-category-button-${slot.department_report_document_id}`}
                              />
                            ) : null}
                          </TableActions>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          )}

          {!loading && total > 0 ? (
            <TablePaginationFooter
              className="mt-3"
              info={`Showing ${start} to ${end} of ${total} results`}
            >
              <PaginationBar
                page={currentPage}
                totalPages={Math.max(1, lastPage)}
                onPage={setPage}
              />
            </TablePaginationFooter>
          ) : null}
        </Modal.Body>
      </Modal>

      <DepartmentReportDocumentModal
        show={editor !== null}
        slot={editor === "new" ? null : editor}
        nextListOrder={nextListOrder}
        busy={busy || writesDisabled}
        availableOriginScopes={availableOriginScopes}
        onHide={() => setEditor(null)}
        onSave={handleSave}
      />

      <ChangeDepartmentReportDocumentOriginModal
        show={originTarget != null}
        onHide={() => setOriginTarget(null)}
        nationId={nationId}
        departmentId={departmentId}
        document={originTarget}
        onChanged={() => void load()}
      />

      <Modal
        show={removeTarget != null}
        onHide={() => !busy && setRemoveTarget(null)}
        centered
        backdrop="static"
        keyboard={!busy}
      >
        <Modal.Header closeButton={!busy}>
          <Modal.Title>Remove document category</Modal.Title>
        </Modal.Header>
        <Modal.Body data-testid="remove-document-confirm">
          This document category will no longer appear on new reports. Existing
          files keep the current name.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setRemoveTarget(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleRemove} disabled={busy}>
            {busy ? <Spinner animation="border" size="sm" aria-hidden /> : "Remove"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
